import { useMemo, useState } from "react";

import { MetricBlock } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import { classFor } from "../data/classes";

/**
 * Tabbed consumables view. One tab per category (Flask / Battle Elixir
 * / etc.). Each tab shows only players who have data in that category,
 * sorted by the category's primary metric (uptime % or cast count).
 *
 * Inside each row the per-spell breakdown is rendered as chips, so a
 * player's row tells you both the headline number and *which*
 * consumable they used to get it.
 */

type CategoryMetric = "uptime" | "casts" | "coverage";

interface CategoryMeta {
  id: string;
  label: string;
  metric: CategoryMetric;
  /** When true, surface a "Casts" column alongside uptime (for drums). */
  showCasts?: boolean;
  /** When false, hide the "% of best" bar column. */
  showBar?: boolean;
  /** Configured spell names for the category — drives per-spell column layouts. */
  spellNames?: string[];
}

interface CategoryCell {
  uptimeMs?: number;
  uptimePct?: number;
  casts?: number;
  /** For metric="coverage": battle slot uptime (flask + battle elixir). */
  battleSlotMs?: number;
  battleSlotPct?: number;
  /** For metric="coverage": guardian slot uptime (flask + guardian elixir). */
  guardianSlotMs?: number;
  guardianSlotPct?: number;
  byName?: Record<string, number>;
}

interface Row {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  categories?: Record<string, CategoryCell>;
}

// Categories temporarily hidden from the sidebar tabs. Keep their data
// computed in the backend so re-enabling is one line.
const HIDDEN_CATEGORIES = new Set<string>([
  // Duration is buggy - flask + elixir slot uptimes don't line up with
  // what the raid actually drank. Hidden until that's fixed.
  "flask_elixir",
]);

export default function Consumables({ block }: { block: MetricBlock }) {
  const rows = (block.rows as unknown as Row[]) ?? [];
  const allCategoryMeta = (block.categoryMeta as unknown as CategoryMeta[]) ?? [];
  const categoryMeta = useMemo(
    () => allCategoryMeta.filter((c) => !HIDDEN_CATEGORIES.has(c.id)),
    [allCategoryMeta],
  );
  const totalTimeMs = (block.totalTimeMs as number | undefined) ?? 0;

  const [selected, setSelected] = useState<string>(
    () => categoryMeta[0]?.id ?? "",
  );

  if (!categoryMeta.length) {
    return (
      <p className="text-sm text-gray-500">
        No consumable categories configured.
      </p>
    );
  }
  if (!rows.length) {
    return (
      <p className="text-sm text-gray-500">
        No consumable data — re-import the report.
      </p>
    );
  }

  const activeCat =
    categoryMeta.find((c) => c.id === selected) ?? categoryMeta[0];

  return (
    <div className="space-y-3">
      <CategoryTabs
        categories={categoryMeta}
        selected={activeCat.id}
        onSelect={setSelected}
      />

      {activeCat.metric === "coverage" ? (
        <CoverageTable
          category={activeCat}
          rows={rows}
          totalTimeMs={totalTimeMs}
        />
      ) : activeCat.id === "scrolls" ? (
        <ScrollsTable
          category={activeCat}
          rows={rows}
          totalTimeMs={totalTimeMs}
        />
      ) : (
        <CategoryTable
          category={activeCat}
          rows={rows}
          totalTimeMs={totalTimeMs}
        />
      )}
    </div>
  );
}

/* ----------------------------- Tabs ----------------------------- */

function CategoryTabs({
  categories,
  selected,
  onSelect,
}: {
  categories: CategoryMeta[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((c) => (
        <button
          key={c.id}
          onClick={() => onSelect(c.id)}
          className={[
            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
            "transition-colors max-w-[14rem]",
            c.id === selected
              ? "border-accent/40 bg-accent/15 text-accent"
              : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
          ].join(" ")}
        >
          <span className="truncate">{c.label}</span>
        </button>
      ))}
    </div>
  );
}

/* --------------------- Flask & Elixirs coverage --------------------- */

interface CoverageRow {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  battlePct: number;
  guardianPct: number;
  battleMs: number;
  guardianMs: number;
  byName: Record<string, number>;
}

function CoverageTable({
  category,
  rows,
  totalTimeMs,
}: {
  category: CategoryMeta;
  rows: Row[];
  totalTimeMs: number;
}) {
  const filtered = useMemo<CoverageRow[]>(() => {
    const out: CoverageRow[] = [];
    for (const r of rows) {
      const cell = r.categories?.[category.id];
      if (!cell) continue;
      const battleMs = cell.battleSlotMs ?? 0;
      const guardianMs = cell.guardianSlotMs ?? 0;
      // Hide players who used absolutely nothing — keeps the table
      // about the people who actually consumed.
      if (
        battleMs <= 0 &&
        guardianMs <= 0 &&
        Object.keys(cell.byName ?? {}).length === 0
      ) {
        continue;
      }
      out.push({
        name: r.name,
        type: r.type,
        icon: r.icon,
        spec: r.spec,
        battleMs,
        guardianMs,
        battlePct: cell.battleSlotPct ?? 0,
        guardianPct: cell.guardianSlotPct ?? 0,
        byName: cell.byName ?? {},
      });
    }
    // Sort by the *worse* slot ascending so anyone who skipped a slot
    // surfaces at the top of the list — that's the "did they forget?"
    // signal.
    out.sort(
      (a, b) =>
        Math.min(a.battlePct, a.guardianPct) -
        Math.min(b.battlePct, b.guardianPct),
    );
    return out;
  }, [rows, category]);

  if (!filtered.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <p className="text-sm text-gray-500">
          No flask or elixir activity in this raid.
        </p>
      </section>
    );
  }

  const rankPad = String(filtered.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th
                className="pb-2 pr-3 font-medium text-right"
                title="Flask + Battle Elixir uptime — together they fill the battle slot"
              >
                Battle slot
              </th>
              <th
                className="pb-2 pr-3 font-medium text-right"
                title="Flask + Guardian Elixir uptime — together they fill the guardian slot"
              >
                Guardian slot
              </th>
              <th className="pb-2 pr-4 font-medium">Used</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="py-2 pl-4 pr-3 text-gray-500 tabular-nums align-top">
                    {padded}
                  </td>
                  <td className="py-2 pr-3 max-w-[14rem] align-top">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-2 pr-3 text-right align-top">
                    <SlotPct ms={r.battleMs} pct={r.battlePct} />
                  </td>
                  <td className="py-2 pr-3 text-right align-top">
                    <SlotPct ms={r.guardianMs} pct={r.guardianPct} />
                  </td>
                  <td className="py-2 pr-4 align-top">
                    <ChipList
                      byName={r.byName}
                      metric="uptime"
                      totalTimeMs={totalTimeMs}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SlotPct({ ms, pct }: { ms: number; pct: number }) {
  if (ms <= 0) {
    return (
      <span className="font-medium text-red-400" title="Slot was empty">
        —
      </span>
    );
  }
  const tone =
    pct >= 95
      ? "text-emerald-300"
      : pct >= 80
        ? "text-amber-300"
        : pct >= 50
          ? "text-orange-300"
          : "text-red-300";
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return (
    <span
      className={"font-medium tabular-nums " + tone}
      title={`active for ${m}:${s.toString().padStart(2, "0")}`}
    >
      {pct.toFixed(0)}%
    </span>
  );
}

/* ------------------------- Scrolls grid ------------------------- */

interface ScrollRow {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  perScroll: number[];
  total: number;
}

function ScrollsTable({
  category,
  rows,
  totalTimeMs,
}: {
  category: CategoryMeta;
  rows: Row[];
  totalTimeMs: number;
}) {
  // Group spell names by their short label so rank-V scrolls collapse
  // into a single "Agility" / "Strength" column. Lower ranks stay as
  // their own column ("Agility IV") so a lesser-rank scroll stands out
  // — but only when at least one player actually used it. Otherwise
  // the table would dedicate a column to every configured rank, even
  // for ranks nobody touched.
  const { columns, columnSpells } = useMemo(() => {
    const fromMeta = category.spellNames ?? [];
    const source = fromMeta.length
      ? fromMeta
      : (() => {
          const seen = new Set<string>();
          for (const r of rows) {
            const cell = r.categories?.[category.id];
            for (const k of Object.keys(cell?.byName ?? {})) seen.add(k);
          }
          return Array.from(seen);
        })();
    const groups = new Map<string, string[]>();
    for (const sn of source) {
      const label = shortScrollLabel(sn);
      const list = groups.get(label) ?? [];
      list.push(sn);
      groups.set(label, list);
    }
    // Drop columns where no row has any uptime — keeps the table tight
    // when, say, nobody used rank IV.
    const allCols = Array.from(groups.keys());
    const usedCols: string[] = [];
    const usedSpells: string[][] = [];
    for (const c of allCols) {
      const spells = groups.get(c) ?? [];
      let used = false;
      for (const r of rows) {
        const by = r.categories?.[category.id]?.byName ?? {};
        if (spells.some((sn) => (by[sn] ?? 0) > 0)) {
          used = true;
          break;
        }
      }
      if (used) {
        usedCols.push(c);
        usedSpells.push(spells);
      }
    }
    const order = usedCols
      .map((c, i) => ({ c, spells: usedSpells[i] }))
      .sort((a, b) => a.c.localeCompare(b.c));
    return {
      columns: order.map((o) => o.c),
      columnSpells: order.map((o) => o.spells),
    };
  }, [category, rows]);

  const filtered = useMemo<ScrollRow[]>(() => {
    const out: ScrollRow[] = [];
    for (const r of rows) {
      const cell = r.categories?.[category.id];
      const byName = cell?.byName ?? {};
      const perScroll = columnSpells.map((spellList) =>
        spellList.reduce((sum, name) => sum + (byName[name] ?? 0), 0),
      );
      const total = perScroll.reduce((a, b) => a + b, 0);
      // Always include definitively-physical specs (so we can see who
      // skipped scrolls). Hybrid classes with an unidentified spec
      // (e.g. Paladin/Paladin with no sub-spec) only show up if they
      // actually used a scroll — better than guessing wrong.
      const definitelyPhysical = isPhysicalSpec(r.type, r.spec);
      const definitelyCasterHealer = isCasterOrHealer(r.type, r.spec);
      if (!definitelyPhysical) {
        if (definitelyCasterHealer) continue;
        if (total <= 0) continue;
      }
      out.push({
        name: r.name,
        type: r.type,
        icon: r.icon,
        spec: r.spec,
        perScroll,
        total,
      });
    }
    // Sort by total desc; ties broken by name so zero-scroll players
    // land in a stable order at the bottom.
    out.sort((a, b) => b.total - a.total || (a.name ?? "").localeCompare(b.name ?? ""));
    return out;
  }, [rows, category, columnSpells]);

  if (!filtered.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <p className="text-sm text-gray-500">
          No one in this raid used a scroll.
        </p>
      </section>
    );
  }

  const rankPad = String(filtered.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              {columns.map((name) => (
                <th
                  key={name}
                  className="pb-2 pr-3 font-medium text-right whitespace-nowrap"
                >
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="py-2 pl-4 pr-3 text-gray-500 tabular-nums align-top">
                    {padded}
                  </td>
                  <td className="py-2 pr-3 max-w-[14rem] align-top">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  {r.perScroll.map((ms, j) => (
                    <td
                      key={columns[j]}
                      className="py-2 pr-3 text-right tabular-nums align-top"
                    >
                      <ScrollCell ms={ms} totalTimeMs={totalTimeMs} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ScrollCell({
  ms,
  totalTimeMs,
}: {
  ms: number;
  totalTimeMs: number;
}) {
  if (ms <= 0) return <span className="text-gray-700">-</span>;
  const pct = totalTimeMs > 0 ? (ms / totalTimeMs) * 100 : 0;
  const tone =
    pct >= 95
      ? "text-emerald-300"
      : pct >= 80
        ? "text-amber-300"
        : pct >= 50
          ? "text-orange-300"
          : "text-red-300";
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return (
    <span
      className={"font-medium " + tone}
      title={`active for ${m}:${s.toString().padStart(2, "0")}`}
    >
      {pct.toFixed(0)}%
    </span>
  );
}

/**
 * Trim the "Scroll of " prefix and the rank-V suffix (V is the top rank
 * and the expected default — its column is just "Agility" / "Strength").
 * Lower ranks keep their Roman numeral so a Scroll of Agility IV stands
 * out as its own column when someone shows up with the cheaper variant.
 */
function shortScrollLabel(name: string): string {
  const noPrefix = name.replace(/^Scroll of\s+/i, "").trim();
  return noPrefix.replace(/\s+V$/, "").trim();
}

/**
 * True for physical-DPS / tank specs that actually benefit from
 * stat scrolls. Caster/healer specs are hidden from the scrolls
 * tab so the table only lists people who *should* have scrolled.
 */
function isPhysicalSpec(
  type: string | undefined | null,
  spec: string | undefined | null,
): boolean {
  if (type === "Mage" || type === "Priest" || type === "Warlock") return false;
  if (type === "Warrior" || type === "Rogue" || type === "Hunter") return true;
  switch (spec ?? "") {
    case "Druid-Feral":
    case "Paladin-Protection":
    case "Paladin-Retribution":
    case "Shaman-Enhancement":
      return true;
    default:
      return false;
  }
}

/** Inverse: spec is definitively caster/healer and shouldn't see scrolls. */
function isCasterOrHealer(
  type: string | undefined | null,
  spec: string | undefined | null,
): boolean {
  if (type === "Mage" || type === "Priest" || type === "Warlock") return true;
  switch (spec ?? "") {
    case "Druid-Balance":
    case "Druid-Restoration":
    case "Paladin-Holy":
    case "Shaman-Elemental":
    case "Shaman-Restoration":
      return true;
    default:
      return false;
  }
}

/* ----------------------------- Table ----------------------------- */

interface TableRow {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  /** Headline value: ms (for uptime) or count (for casts). */
  value: number;
  /** Total time the report covers — used to compute uptime %. */
  uptimePct?: number;
  /** Cast count alongside uptime — used by `showCasts` columns. */
  casts?: number;
  /** Per-spell breakdown for chips. Values are ms (uptime) or counts (casts). */
  byName: Record<string, number>;
}

function CategoryTable({
  category,
  rows,
  totalTimeMs,
}: {
  category: CategoryMeta;
  rows: Row[];
  totalTimeMs: number;
}) {
  const filtered = useMemo<TableRow[]>(() => {
    const out: TableRow[] = [];
    for (const r of rows) {
      const cell = r.categories?.[category.id];
      if (!cell) continue;
      const byName = cell.byName ?? {};
      let value = 0;
      let uptimePct: number | undefined;
      if (category.metric === "uptime") {
        value = cell.uptimeMs ?? 0;
        const cellCasts = cell.casts ?? 0;
        if (value <= 0 && cellCasts <= 0 && Object.keys(byName).length === 0) {
          continue;
        }
        uptimePct =
          totalTimeMs > 0 ? (value / totalTimeMs) * 100 : 0;
        out.push({
          name: r.name,
          type: r.type,
          icon: r.icon,
          spec: r.spec,
          value,
          uptimePct,
          casts: cellCasts,
          byName,
        });
        continue;
      }
      value = cell.casts ?? 0;
      if (value <= 0) continue;
      out.push({
        name: r.name,
        type: r.type,
        icon: r.icon,
        spec: r.spec,
        value,
        uptimePct,
        byName,
      });
    }
    out.sort((a, b) => b.value - a.value);
    return out;
  }, [rows, category, totalTimeMs]);

  if (!filtered.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <p className="text-sm text-gray-500">
          No one in this raid used a {category.label.toLowerCase()}.
        </p>
      </section>
    );
  }

  const top = filtered[0]?.value ?? 0;
  const rankPad = String(filtered.length).length;
  const showBar = category.showBar !== false;
  const showCasts = category.showCasts === true && category.metric === "uptime";

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium text-right">
                {category.metric === "uptime" ? "Uptime" : "Casts"}
              </th>
              {showCasts && (
                <th className="pb-2 pr-3 font-medium text-right">Casts</th>
              )}
              {showBar && (
                <th className="pb-2 pr-3 font-medium w-1/4">% of best</th>
              )}
              <th className="pb-2 pr-4 font-medium">Used</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const cls = classFor(r.type);
              const padded = String(i + 1).padStart(rankPad, "0");
              const pct = top > 0 ? (r.value / top) * 100 : 0;
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="py-2 pl-4 pr-3 text-gray-500 tabular-nums align-top">
                    {padded}
                  </td>
                  <td className="py-2 pr-3 max-w-[14rem] align-top">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums align-top">
                    {category.metric === "uptime" ? (
                      <UptimeValue
                        ms={r.value}
                        pct={r.uptimePct ?? 0}
                      />
                    ) : (
                      <span className="font-medium text-gray-100">
                        {r.value}
                      </span>
                    )}
                  </td>
                  {showCasts && (
                    <td className="py-2 pr-3 text-right tabular-nums align-top">
                      {r.casts && r.casts > 0 ? (
                        <span className="font-medium text-gray-200">
                          {r.casts}
                        </span>
                      ) : (
                        <span className="text-gray-700">-</span>
                      )}
                    </td>
                  )}
                  {showBar && (
                    <td className="py-2 pr-3 align-top">
                      <ProgressBar pct={pct} color={cls.color} />
                    </td>
                  )}
                  <td className="py-2 pr-4 align-top">
                    <ChipList
                      byName={r.byName}
                      metric={category.metric}
                      totalTimeMs={totalTimeMs}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UptimeValue({ ms, pct }: { ms: number; pct: number }) {
  const tone =
    pct >= 95
      ? "text-emerald-300"
      : pct >= 80
        ? "text-amber-300"
        : pct >= 50
          ? "text-orange-300"
          : "text-red-300";
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return (
    <span
      className={"font-medium " + tone}
      title={`active for ${m}:${s.toString().padStart(2, "0")}`}
    >
      {pct.toFixed(0)}%
    </span>
  );
}

function ChipList({
  byName,
  metric,
  totalTimeMs,
}: {
  byName: Record<string, number>;
  metric: CategoryMetric;
  totalTimeMs: number;
}) {
  const entries = Object.entries(byName)
    .map(([name, value]) => ({ name, value }))
    .filter((e) => e.value > 0)
    .sort((a, b) => b.value - a.value);
  if (!entries.length) return <span className="text-gray-700">-</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {entries.map((e) => (
        <Chip
          key={e.name}
          name={e.name}
          value={e.value}
          metric={metric}
          totalTimeMs={totalTimeMs}
        />
      ))}
    </div>
  );
}

function Chip({
  name,
  value,
  metric,
  totalTimeMs,
}: {
  name: string;
  value: number;
  metric: CategoryMetric;
  totalTimeMs: number;
}) {
  if (metric === "uptime") {
    const pct = totalTimeMs > 0 ? (value / totalTimeMs) * 100 : 0;
    const tone =
      pct >= 95
        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/20"
        : pct >= 80
          ? "bg-amber-500/15 text-amber-300 border-amber-500/20"
          : pct >= 50
            ? "bg-orange-500/15 text-orange-300 border-orange-500/20"
            : "bg-red-500/15 text-red-300 border-red-500/20";
    const sec = Math.round(value / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return (
      <span
        className={
          "inline-flex items-baseline gap-1.5 rounded border px-1.5 py-0.5 " +
          "text-[11px] font-medium max-w-[14rem] " +
          tone
        }
        title={`${name} - active for ${m}:${s.toString().padStart(2, "0")} (${pct.toFixed(1)}%)`}
      >
        <span className="truncate">{name}</span>
        <span className="text-[10px] opacity-90 tabular-nums flex-shrink-0">
          {pct.toFixed(0)}%
        </span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-baseline gap-1.5 rounded border border-border/60 bg-bg/40 px-1.5 py-0.5 text-[11px] font-medium text-gray-200 max-w-[14rem]"
      title={`${name} - used ${value} time${value === 1 ? "" : "s"}`}
    >
      <span className="truncate">{name}</span>
      <span className="text-[10px] opacity-80 tabular-nums flex-shrink-0">
        ×{value}
      </span>
    </span>
  );
}
